import { type ReactNode } from 'react';
import { toast, type ToastOptions, type Id } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions extends ToastOptions {
  description?: string;
}

/**
 * SystemNotification - A centralized notification system for the application.
 * Uses react-toastify library under the hood for maximum customization.
 *
 * @example
 * // Success notification
 * SystemNotification.success("Operação realizada com sucesso!");
 *
 * // Error notification
 * SystemNotification.error("Erro ao processar a requisição.");
 *
 * // Warning notification
 * SystemNotification.warning("Atenção: Esta ação não pode ser desfeita.");
 *
 * // Info notification
 * SystemNotification.info("Nova atualização disponível.");
 *
 * // With options
 * SystemNotification.success("Salvo!", {
 *   autoClose: 5000,
 *   position: "top-center",
 * });
 *
 * // With custom className
 * SystemNotification.success("Customizado!", {
 *   className: "custom-toast",
 *   progressClassName: "custom-progress",
 * });
 */
export const SystemNotification = {
  /**
   * Shows a success notification
   * @param message - The main message to display
   * @param options - Additional options for the notification
   * @returns Toast ID
   */
  success: (message: string, options?: NotificationOptions): Id => {
    return toast.success(message, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    });
  },

  /**
   * Shows an error notification
   * @param message - The main message to display
   * @param options - Additional options for the notification
   * @returns Toast ID
   */
  error: (message: string, options?: NotificationOptions): Id => {
    return toast.error(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    });
  },

  /**
   * Shows a warning notification
   * @param message - The main message to display
   * @param options - Additional options for the notification
   * @returns Toast ID
   */
  warning: (message: string, options?: NotificationOptions): Id => {
    return toast.warning(message, {
      position: 'top-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    });
  },

  /**
   * Shows an info notification
   * @param message - The main message to display
   * @param options - Additional options for the notification
   * @returns Toast ID
   */
  info: (message: string, options?: NotificationOptions): Id => {
    return toast.info(message, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    });
  },

  /**
   * Shows a loading notification that can be updated later
   * @param message - The loading message to display
   * @param options - Additional options for the notification
   * @returns The toast ID that can be used to update/dismiss the notification
   */
  loading: (message: string, options?: NotificationOptions): Id => {
    return toast.loading(message, {
      position: 'top-right',
      ...options,
    });
  },

  /**
   * Updates an existing toast notification
   * @param toastId - The toast ID to update
   * @param message - The new message
   * @param type - The notification type
   * @param options - Additional options
   */
  update: (toastId: Id, message: string, type: NotificationType, options?: NotificationOptions) => {
    toast.update(toastId, {
      render: message,
      type,
      isLoading: false,
      autoClose: 3000,
      ...options,
    });
  },

  /**
   * Shows a promise-based notification that updates based on promise state
   * @param promise - The promise to track
   * @param messages - Messages for pending, success, and error states
   * @param options - Additional options for the notification
   */
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      pending: string;
      success: string;
      error: string;
    },
    options?: NotificationOptions,
  ) => {
    return toast.promise(promise, messages, {
      position: 'top-right',
      ...options,
    });
  },

  /**
   * Dismisses a specific notification or all notifications
   * @param toastId - Optional toast ID to dismiss a specific notification
   */
  dismiss: (toastId?: Id) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },

  /**
   * Shows a custom/default notification
   * @param message - The message to display
   * @param options - Additional options for the notification
   * @returns Toast ID
   */
  custom: (message: ReactNode, options?: NotificationOptions): Id => {
    return toast(message, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    });
  },
};

export default SystemNotification;
